import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './post-login-welcome.css';

const STORAGE_KEY = 'saloni_post_login_welcome';
/** Visible duration before fade-out (ms). */
const TOAST_VISIBLE_MS = 1800;
const TOAST_FADE_MS = 320;

const AUTO_USERNAME_SUFFIX = /_[a-f0-9]{6,}$/i;

function formatName(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** Prefer profile display name; avoid auto-generated usernames like user_f1eb99. */
export function resolveWelcomeName({ displayName, username, email } = {}) {
  const profileName = String(displayName || '').trim();
  if (profileName) return formatName(profileName);

  const emailLocal = String(email || '')
    .split('@')[0]
    ?.replace(/[._+-]+/g, ' ')
    .trim();
  if (emailLocal && emailLocal.length >= 2 && !AUTO_USERNAME_SUFFIX.test(emailLocal)) {
    return formatName(emailLocal);
  }

  let user = String(username || '').trim();
  if (!user) return '';

  user = user.replace(AUTO_USERNAME_SUFFIX, '');
  user = user.replace(/\d.*$/, '').replace(/[._+-]+/g, ' ').trim();
  if (!user) return '';

  const word = user.match(/^([A-Za-z]+)/)?.[1];
  if (word && word.length >= 2 && word.length <= 24) {
    return formatName(word);
  }

  return user.length <= 24 ? formatName(user) : '';
}

function displayInitial(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase();
}

/** Call after a successful sign-in, before navigating away from `/login`. */
export function queuePostLoginWelcome(profile) {
  if (typeof window === 'undefined') return;
  const resolved =
    typeof profile === 'string'
      ? formatName(profile)
      : resolveWelcomeName(profile || {});
  if (!resolved) return;
  sessionStorage.setItem(STORAGE_KEY, resolved);
}

export default function PostLoginWelcomeToast() {
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const consumedRef = useRef(false);

  // Read queued welcome once (not tied to dismiss timers).
  useLayoutEffect(() => {
    if (location.pathname === '/login' || consumedRef.current) return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(STORAGE_KEY);
    consumedRef.current = true;
    setToast({ name: raw.slice(0, 120), closing: false });
  }, [location.pathname]);

  // Auto-dismiss — only depends on toast being shown, not on route changes.
  useEffect(() => {
    if (!toast?.name) return undefined;

    const fadeTimer = window.setTimeout(() => {
      setToast((prev) => (prev ? { ...prev, closing: true } : null));
    }, TOAST_VISIBLE_MS - TOAST_FADE_MS);

    const hideTimer = window.setTimeout(() => {
      setToast(null);
    }, TOAST_VISIBLE_MS);

    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(hideTimer);
    };
  }, [toast?.name]);

  if (!toast) return null;

  const name = formatName(toast.name);
  const initial = displayInitial(name);

  return (
    <div
      className={`saloni-welcome-toast${toast.closing ? ' saloni-welcome-toast--out' : ''}`}
      role="status"
      aria-live="polite"
    >
      {initial ? (
        <span className="saloni-welcome-toast__mark" aria-hidden>
          {initial}
        </span>
      ) : null}
      <div className="saloni-welcome-toast__body">
        <p className="saloni-welcome-toast__eyebrow">Welcome back</p>
        {name ? <p className="saloni-welcome-toast__name">{name}</p> : null}
      </div>
      <button
        type="button"
        className="saloni-welcome-toast__close"
        aria-label="Dismiss"
        onClick={() => setToast(null)}
      >
        ×
      </button>
    </div>
  );
}
